class N8nIntegration {
    constructor(config) {
        this.config = config || null;
    }
    /**
     * Configure n8n integration
     */
    configure(config) {
        this.config = config;
    }
    /**
     * Check if n8n integration is configured and enabled
     */
    isEnabled() {
        return !!(this.config && this.config.enabled && this.config.webhookUrl);
    }
    /**
     * Send issue data to n8n webhook
     */
    async sendToN8n(issueData) {
        if (!this.config || !this.config.webhookUrl) {
            return {
                success: false,
                message: "n8n webhook URL is not configured",
                error: "MISSING_CONFIG",
            };
        }
        if (!this.config.enabled) {
            return {
                success: false,
                message: "n8n integration is disabled",
                error: "DISABLED",
            };
        }
        try {
            // Prepare FormData with binary screenshot
            const formData = await this.prepareFormData(issueData);
            // Setup request headers (don't set Content-Type - browser will set it with boundary for FormData)
            const headers = {
                ...this.config.headers,
            };
            // Setup timeout
            const timeout = this.config.timeout || 30000; // 30 seconds default
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            // Send request
            const response = await fetch(this.config.webhookUrl, {
                method: "POST",
                headers,
                body: formData,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            // Check response
            if (!response.ok) {
                const errorText = await response.text().catch(() => "Unknown error");
                return {
                    success: false,
                    message: `n8n webhook request failed: ${response.status} ${response.statusText}`,
                    error: errorText,
                };
            }
            // Parse response
            let responseData;
            try {
                responseData = await response.json();
            }
            catch (e) {
                // Some webhooks don't return JSON
                responseData = { message: "Success" };
            }
            return {
                success: true,
                message: "Issue sent to n8n successfully",
                workflowId: responseData.workflowId || responseData.executionId,
            };
        }
        catch (error) {
            if (error instanceof Error) {
                if (error.name === "AbortError") {
                    return {
                        success: false,
                        message: "Request to n8n webhook timed out",
                        error: "TIMEOUT",
                    };
                }
                return {
                    success: false,
                    message: `Failed to send issue to n8n: ${error.message}`,
                    error: error.message,
                };
            }
            return {
                success: false,
                message: "Unknown error occurred while sending to n8n",
                error: "UNKNOWN",
            };
        }
    }
    /**
     * Convert base64 data URL to Blob
     */
    base64ToBlob(base64Data) {
        // Extract the base64 string and mime type
        const matches = base64Data.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
            throw new Error("Invalid base64 data URL");
        }
        const mimeType = matches[1];
        const base64String = matches[2];
        // Decode base64
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }
    /**
     * Prepare FormData payload for n8n webhook
     * This sends the screenshot as a binary file instead of base64 JSON
     */
    async prepareFormData(issueData) {
        const formData = new FormData();
        // Add issue data as JSON string
        formData.append("title", issueData.title);
        formData.append("description", issueData.description);
        formData.append("priority", issueData.priority);
        formData.append("deviceType", issueData.deviceType);
        formData.append("createdAt", issueData.createdAt);
        // Convert base64 screenshot to binary Blob and add as file
        const screenshotBlob = this.base64ToBlob(issueData.screenshot);
        const filename = `screenshot-${Date.now()}.png`;
        formData.append("screenshot", screenshotBlob, filename);
        // Add basic device info as individual fields
        formData.append("browser", issueData.deviceInfo.browser);
        formData.append("os", issueData.deviceInfo.os);
        formData.append("screenResolution", issueData.deviceInfo.screenResolution);
        formData.append("viewport", issueData.deviceInfo.viewport);
        formData.append("url", issueData.deviceInfo.url);
        formData.append("userAgent", issueData.deviceInfo.userAgent);
        formData.append("language", issueData.deviceInfo.language || "");
        formData.append("platform", issueData.deviceInfo.platform);
        formData.append("cookiesEnabled", String(issueData.deviceInfo.cookiesEnabled));
        formData.append("onLine", String(issueData.deviceInfo.onLine));
        formData.append("timestamp", issueData.deviceInfo.timestamp);
        // Add extended debugging information as JSON strings
        if (issueData.deviceInfo.technologies) {
            formData.append("technologies", JSON.stringify(issueData.deviceInfo.technologies));
        }
        if (issueData.deviceInfo.consoleLogs) {
            formData.append("consoleLogs", JSON.stringify(issueData.deviceInfo.consoleLogs));
        }
        if (issueData.deviceInfo.debugInfo) {
            formData.append("debugInfo", JSON.stringify(issueData.deviceInfo.debugInfo));
        }
        // Add navigation steps if available
        if (issueData.deviceInfo.navigationSteps && issueData.deviceInfo.navigationSteps.length > 0) {
            formData.append("navigationSteps", JSON.stringify(issueData.deviceInfo.navigationSteps));
            formData.append("navigationStepsCount", String(issueData.deviceInfo.navigationSteps.length));
        }
        // Add metadata
        formData.append("source", "uxbert-aminah");
        formData.append("version", "1.0.4");
        return formData;
    }
    /**
     * Test connection to n8n webhook
     */
    async testConnection() {
        if (!this.config || !this.config.webhookUrl) {
            return {
                success: false,
                message: "n8n webhook URL is not configured",
                error: "MISSING_CONFIG",
            };
        }
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for test
            await fetch(this.config.webhookUrl, {
                method: "HEAD",
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return {
                success: true,
                message: "n8n webhook is reachable",
            };
        }
        catch (error) {
            return {
                success: false,
                message: "Failed to reach n8n webhook",
                error: error instanceof Error ? error.message : "UNKNOWN",
            };
        }
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return this.config;
    }
    /**
     * Update webhook URL
     */
    setWebhookUrl(url) {
        if (!this.config) {
            this.config = {
                webhookUrl: url,
                enabled: true,
            };
        }
        else {
            this.config.webhookUrl = url;
        }
    }
    /**
     * Enable/disable n8n integration
     */
    setEnabled(enabled) {
        if (this.config) {
            this.config.enabled = enabled;
        }
    }
}
export default N8nIntegration;
//# sourceMappingURL=n8n-integration.js.map